import {useEffect} from 'react'
import {useAddBanner, useDismissBanner, type InitialBanner} from '@github-ui/banner-stack'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Banner} from '@primer/react'
import type {ActionsSurveyPayload} from '../../routes/workflows-layout-route'
import {useActionsAnalytics} from '../../hooks/use-actions-analytics'

export const ACTIONS_SURVEY_BANNER_ID = 'actions-survey'

type ActionsSurveyBanner = InitialBanner & {'data-test-selector'?: string}

function ActionsSurveyPrimaryAction({
  surveyLink,
  surveyOpenCallbackPath,
}: Pick<ActionsSurveyPayload, 'surveyLink' | 'surveyOpenCallbackPath'>) {
  const dismissBanner = useDismissBanner()
  const track = useActionsAnalytics()

  const handleOpen = () => {
    track('workflow_banner.open_survey', 'ref_loc:actions_survey_banner')
    // Recording that the survey was opened is best-effort housekeeping — fire it off
    // without waiting so a slow/failed request never delays the user reaching the survey.
    void recordSurveyOpened(surveyOpenCallbackPath)

    dismissBanner(ACTIONS_SURVEY_BANNER_ID)
    if (ssrSafeWindow) {
      ssrSafeWindow.location.href = surveyLink
    }
  }

  return <Banner.PrimaryAction onClick={handleOpen}>Give feedback</Banner.PrimaryAction>
}

async function recordSurveyOpened(surveyOpenCallbackPath: string) {
  try {
    await verifiedFetch(surveyOpenCallbackPath, {method: 'POST'})
  } catch {
    // Best-effort: nothing to do if this fails.
  }
}

export function getActionsSurveyBanner({
  surveyLink,
  surveyOpenCallbackPath,
  surveyDismissCallbackPath,
}: ActionsSurveyPayload): ActionsSurveyBanner {
  return {
    id: ACTIONS_SURVEY_BANNER_ID,
    title: 'Help us improve GitHub Actions',
    description: 'Tell us how to make GitHub Actions work better for you with three quick questions.',
    variant: 'info',
    dismissible: true,
    onDismiss: async () => {
      const response = await verifiedFetch(surveyDismissCallbackPath, {method: 'POST'})
      if (!response.ok) {
        throw new Error(`Failed to dismiss the Actions survey banner (${response.status})`)
      }
    },
    'data-test-selector': ACTIONS_SURVEY_BANNER_ID,
    primaryAction: (
      <ActionsSurveyPrimaryAction surveyLink={surveyLink} surveyOpenCallbackPath={surveyOpenCallbackPath} />
    ),
  }
}

export function useActionsSurvey({data}: {data?: ActionsSurveyPayload}) {
  const addBanner = useAddBanner()
  const dismissBanner = useDismissBanner()

  useEffect(() => {
    if (!data) {
      dismissBanner(ACTIONS_SURVEY_BANNER_ID)
      return
    }

    const id = addBanner(getActionsSurveyBanner(data))

    return () => {
      dismissBanner(id)
    }
  }, [data, addBanner, dismissBanner])
}
